// Low-level localStorage draft I/O shared by the RouteOperations draft hooks
// (useRouteEdits, useDailyChecklistDraft). Centralizes the three JSON +
// try/catch patterns so the storage-failure policy lives in one place:
//   - read  → return parsed value, or null on missing/parse error
//   - write → JSON.stringify, warn (don't throw) if storage is full/blocked
//   - remove → best-effort, ignore errors
//
// Callers own key construction and any higher-level semantics (staleness
// tags, server seeding, debounce coordination).

// Returns the parsed value at `key`, or null when the key is empty/missing or
// the stored value can't be parsed.
export function readDraft(key) {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    // eslint-disable-next-line no-console -- reason: a parse/read failure on a
    // key that DOES hold a value is a corrupt draft, not "no draft" — surface
    // it so the two cases aren't indistinguishable in the console.
    console.warn(`Failed to read localStorage draft "${key}"`, e);
    return null;
  }
}

// Persists `value` (JSON-serialized) at `key`. Storage failures (quota,
// private mode) are logged via `warnLabel` and swallowed so the UI never
// breaks on a draft write.
export function writeDraft(key, value, warnLabel) {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // eslint-disable-next-line no-console -- reason: surface storage-full /
    // blocked-storage failures for debugging without breaking the UI.
    console.warn(warnLabel || 'localStorage draft write failed', e);
  }
}

// Best-effort removal; ignores storage errors.
export function removeDraft(key) {
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    /* ignore */
  }
}
